/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Snigdha Dhanireddy <snigdha.dhanireddy@mroads.com>, June 2023
 * @flow
 */

import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import PropTypes from 'prop-types';
import useStyles from './useStyles';
import { Typography, Button, IconButton, Accordion, AccordionDetails, AccordionSummary, Box, Icon } from '@material-ui/core';
import {
    HighlightOff as HighlightOffIcon,
    ExpandMore as ExpandMoreIcon,
    SmsOutlined as SmsIcon,
    CallMergeOutlined as MergeIcon,
    CallSplitOutlined as UnmergeIcon,
    SwapHorizOutlined as TransferIcon,
    EventBusyOutlined as VoidCheckIcon,
    CancelScheduleSendOutlined as ClearIcon,
} from '@material-ui/icons';
import TablesLookup from '../TablesLookup';
import Tabs from '../../../../components/Tabs';
import Aux from '../../../../components/AuxComponent';
import CommonUtil from '../../../Fireboard/common';
import MenuUtils from '../../../../util/menu-utils';
import CheckDetails from '../CheckDetails';

const TableProperties = ({
    goToWaitlistScreen,
    selectedItem,
    selectedShape,
    selectedTableChecks,
    onVoidCheck,
    unseatGuest,
    mergeTable,
    unmergeTable,
    onClickSms,
    onTableSelection,
    activeTableAction,
    onClickTableAction,
    clearAllTableAction,
    confirmationMessage,
    onContinue,
    initiateMoveTable,
    tableActionEnabledCriteria,
    stations,
    updatedShapes,
}) => {
    const classes = useStyles();
    const { labelsData } = useSelector(state => state);
    const { labels: strings } = labelsData;
    const [expanded, setExpanded] = React.useState(false);
    const [selectedCheck, setSelectedCheck] = React.useState({});

    const { storeInfo, currencyInfo } = useSelector(state => state);
    const { currency } = currencyInfo || {};
    const { serverAppFeatures } = storeInfo?.data || {};
    const { suppressTipsOnFireBoard = false } = serverAppFeatures || {};

    useEffect(() => {
        if (Array.isArray(selectedTableChecks) && selectedTableChecks.length > 0)
            setSelectedCheck(selectedTableChecks[0]);
    }, [selectedTableChecks]);

    const handleChange = () => {
        setExpanded(!expanded);
    };

    const onVoidCheckClick = () => {
        onClickTableAction('voidCheck');
        onVoidCheck();
    }

    const onSms = () => {
        onClickTableAction('sms');
        onClickSms && onClickSms();
    };

    const onTransferTable = () => {
        initiateMoveTable && initiateMoveTable();
    };


    const onContinueMerge = () => {
        let stationIds = [];
        Array.from(updatedShapes.values()).forEach(table => {
            if (table.selected && table.stationId !== selectedShape.stationId) {
                stationIds.push(table.stationId);
            }
        });
        if (stationIds.length > 0) {
            mergeTable(selectedShape.stationId, stationIds);
        }
    }

    const onContinueUnmerge = () => {
        if (stations !== '') {
            unmergeTable();
        }
    }

    const onTabChange = id => {
        if (Array.isArray(selectedTableChecks)) {
            const check = selectedTableChecks.find(el => el.id === id);
            if (check) setSelectedCheck(check);
        }
    };

    const getItems = (check) => {
        let items = check && check.items;
        let pendingItems = [];
        if (CommonUtil.getOrderTypeFromSourceType(check.sourceType) === 'POS') {
            pendingItems = MenuUtils.getPendingItemsFromCheck(check);
        }
        return { items, pendingItems };
    };


    const isTableActionVisible = (key) => {
        const { clearTableEnabled, mergeTableEnabled, unmergeTableEnabled, transferTableEnabled, voidCheckEnabled } = tableActionEnabledCriteria;
        if (key === 'clearTable') {
            return clearTableEnabled;
        } else if (key === 'mergeTable') {
            return mergeTableEnabled;
        } else if (key === 'unmergeTable') {
            return unmergeTableEnabled;
        } else if (key === 'transferTable') {
            return transferTableEnabled;
        } else if (key === 'voidCheck') {
            return voidCheckEnabled;
        }
        return false;
    };

    const getMergedStations = () => {
        let stationNumbers = [];
        Array.from(updatedShapes.values()).forEach(shape => {
            if (shape.selected && shape.stationId !== selectedShape.stationId) {
                stationNumbers.push(shape.stationNumber);
            }
        });
        return stationNumbers.length > 0
            ? `${stationNumbers.toString()} to ${selectedShape?.stationNumber}`
            : null;
    };

    const isTranferDisabled = () => {
        const filteredShapes = Array.from(updatedShapes.values()).filter(it => it.stationId && it.selected);
        return filteredShapes.length === 0;
    };

    return (
        <Box height={'100vh'} overflow={'scroll'}>
            <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'} className={classes.header}>
                <Typography variant='h6' className={classes.title}>{strings.PROPERTIES_LABEL}</Typography>
                <IconButton className={classes.closeImg} onClick={goToWaitlistScreen}>
                    <HighlightOffIcon />
                </IconButton>
            </Box>
            <Box padding={1}>
                <Box mb={1} pl={0.5} pr={0.5} pt={1} pb={1} borderRadius={0.5} display={'flex'} justifyContent={'space-between'} className={classes.actionContainer}>
                    <Box display={'flex'} alignItems={'center'}>
                        <TableActionButton label={strings.PROPERTIES_CLEAR_TABLE} icon={'table_restaurant'} isActive={activeTableAction?.clearTable} onClick={() => onClickTableAction('clearTable')} disabled={!isTableActionVisible('clearTable')} />
                        <TableActionButton label={strings.PROPERTIES_MERGE_TABLE} icon={'merge_type'} isActive={activeTableAction?.mergeTable} onClick={() => onClickTableAction('mergeTable')} disabled={!isTableActionVisible('mergeTable')} />
                        <TableActionButton label={strings.PROPERTIES_UNMERGE_TABLE} icon={'call_split'} isActive={activeTableAction?.unmergeTable} onClick={() => onClickTableAction('unmergeTable')} disabled={!isTableActionVisible('unmergeTable')} />
                    </Box>
                    <Box display={'flex'} alignItems={'center'}>
                        <TableActionButton label={strings.PROPERTIES_TRANSFER_TABLE} icon={'sync_alt'} isActive={activeTableAction?.transferTable} onClick={onTransferTable} disabled={!isTableActionVisible('transferTable')} />
                        <TableActionButton label={strings.PROPERTIES_VOID_CHECK} icon={'playlist_remove'} isActive={activeTableAction?.voidCheck} onClick={onVoidCheckClick} disabled={!isTableActionVisible('voidCheck')} />
                        <TableActionButton label={strings.PROPERTIES_SMS_GUEST} icon={'sms'} isActive={activeTableAction?.sms} onClick={onSms} disabled={false} />
                    </Box>
                </Box>
                <Box height={30} padding={1} borderRadius={0.5} marginBottom={1} display={'flex'} alignItems={'center'} className={classes.tableDetailsContainer}>
                    <Typography variant='body1' className={classes.tableNumber}>Table {selectedShape?.stationNumber} {'{#' + selectedShape?.stationId + '}'}</Typography>
                </Box>
                {activeTableAction?.clearTable ? (
                    <MessageSection
                        cancelMethod={clearAllTableAction}
                        continueMethod={unseatGuest}
                        message={strings.PROPERTIES_CLEAR_MSG}
                    />
                ) : activeTableAction?.mergeTable ? (
                    <React.Fragment>
                        <TablesLookup
                            allTables={Array.from(updatedShapes.values())}
                            onTableSelection={onTableSelection}
                        />
                        <MessageSection
                            cancelMethod={clearAllTableAction}
                            continueMethod={onContinueMerge}
                            message={strings.PROPERTIES_MERGE_TEXT}
                            stationNumbers={getMergedStations()}
                            disabled={getMergedStations() === null}
                        />
                    </React.Fragment>
                ) : activeTableAction?.unmergeTable ? (
                    <React.Fragment>
                        {stations !== '' ? (
                            <MessageSection
                                cancelMethod={clearAllTableAction}
                                continueMethod={onContinueUnmerge}
                                message={strings.PROPERTIES_UNMERGE_TEXT}
                                stationNumbers={stations}
                            />
                        ) : null}
                    </React.Fragment>
                ) : activeTableAction?.transferTable ? (
                    <MessageSection
                        cancelMethod={clearAllTableAction}
                        continueMethod={onContinue}
                        message={confirmationMessage}
                        stationNumbers={''}
                        disabled={isTranferDisabled()}
                    />
                ) : selectedTableChecks && selectedTableChecks.length > 0 ? (
                    <Accordion expanded={expanded} onChange={handleChange} className={classes.checkContainer}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="check-content"
                            id="check-header"
                        >
                            <Typography variant='body2' className={classes.bookingName}>{selectedItem?.name}</Typography>
                            <Typography variant='body2' className={classes.bookingName}>{selectedItem?.phoneNumber}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Tabs
                                activeTab={selectedCheck?.id}
                                onTabChange={onTabChange}
                                tabsContainerStyle={classes.tabsContainer}
                                tabsStyle={classes.ulStyle}
                                showGrey
                            >
                                {selectedTableChecks.map(check => {
                                    const { selectedCheckDetails } = check;
                                    const { items, pendingItems } = getItems(selectedCheckDetails);
                                    return (
                                        <Aux id={check.id} name={check.number} key={`id-${check.id}`}>
                                            <CheckDetails
                                                selectedItem={selectedCheckDetails}
                                                items={items}
                                                pendingItems={pendingItems}
                                                currency={currency}
                                                suppressTipsOnFireBoard={suppressTipsOnFireBoard}
                                            />
                                        </Aux>
                                    )
                                })}
                            </Tabs>
                        </AccordionDetails>
                    </Accordion>
                ) : null
                }
            </Box>
        </Box >
    );
};

const MessageSection = ({ cancelMethod, continueMethod, message, stationNumbers, disabled }) => {
    const classes = useStyles();
    const { labelsData } = useSelector(state => state);
    const { labels: strings } = labelsData;
    return (
        <Box width={'100%'} display={'flex'} flexDirection={'column'} justifyContent={'center'} position={'absolute'} bottom={10}>
            <Typography variant='body2' className={classes.message}>{message} {stationNumbers ? stationNumbers : ''}</Typography>
            <Typography variant='body2' className={classes.message}>{strings.PROPERTIES_CONTINUE_MSG}</Typography>
            <Box display={'flex'} flexDirection={'row'} justifyContent={'space-around'} marginTop={1.2}>
                <Button
                    variant="outlined"
                    color='primary'
                    onClick={cancelMethod}
                    className={classes.secondaryButton}
                >
                    {strings.PROPERTIES_CANCEL}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={continueMethod}
                    className={classes.button}
                    disabled={disabled}
                >
                    {strings.PROPERTIES_CONTINUE}
                </Button>
            </Box>
        </Box>
    )
}

export default TableProperties;

TableProperties.propTypes = {
    goToWaitlistScreen: PropTypes.func,
};

TableProperties.defaultProps = {
    goToWaitlistScreen: () => { },
};

const TableActionButton = (props) => {
    const { label, onClick, isActive, disabled, icon } = props;
    const classes = useStyles();
    return (
        <Box
            display={'flex'}
            alignItems={'center'}
            p={0.8}
            m={0.5}
            className={isActive ? cn(classes.actionBtn, classes.selectedActionBtn) : classes.actionBtn}
            onClick={disabled ? null : onClick}
            style={{ opacity: disabled ? 0.4 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
        >
            <Icon className={'material-icons-outlined'} fontSize='small'>{icon}</Icon>
            {isActive && (
                <Typography variant='caption' className={classes.actionBtnTxt}>{label}</Typography>
            )}
        </Box>
    )
};
